import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Check, Search, X, AlertTriangle } from 'lucide-react';

/* Shared furniture for the admin consoles — one place for the page head, the
   stat row, panels, pills and pagination, so every screen lines up. */

export const Panel = ({ className = '', children, ...rest }) => (
  <section
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm
                dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none ${className}`}
    {...rest}
  >
    {children}
  </section>
);

/** Eyebrow + title + subtitle on the left, actions on the right. */
export const PageHead = ({ eyebrow, title, badge, subtitle, actions }) => (
  <Panel className="px-5 sm:px-6 py-5 mb-5">
    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[10.5px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500 mb-1.5">
            {eyebrow}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-[26px] sm:text-[30px] leading-tight font-black text-slate-900 dark:text-white">{title}</h1>
          {badge}
        </div>
        {subtitle && (
          <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-1.5 max-w-3xl">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">{actions}</div>}
    </div>
  </Panel>
);

const TONES = {
  blue:    { ring: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',          bar: 'bg-blue-500' },
  violet:  { ring: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',  bar: 'bg-violet-500' },
  emerald: { ring: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300', bar: 'bg-emerald-500' },
  amber:   { ring: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',      bar: 'bg-amber-500' },
  rose:    { ring: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',          bar: 'bg-rose-500' },
  cyan:    { ring: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300',          bar: 'bg-cyan-500' },
  slate:   { ring: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300',      bar: 'bg-slate-400' },
};

/**
 * One figure with its label. `note` is the muted line under it; `tag` is the
 * small pill beside the number. Nothing here invents a number — pass what the
 * data actually says.
 */
export const StatCard = ({ label, value, tag, note, icon: Icon, tone = 'blue' }) => {
  const t = TONES[tone] || TONES.blue;
  return (
    <Panel className="relative overflow-hidden px-4 sm:px-5 pt-4 pb-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{label}</p>
        {Icon && <span className={`grid place-items-center w-9 h-9 rounded-xl flex-shrink-0 ${t.ring}`}><Icon size={17} /></span>}
      </div>
      <div className="flex items-end gap-2 mt-2">
        <p className="text-[26px] leading-none font-black text-slate-900 dark:text-white">{value}</p>
        {tag}
      </div>
      {note && <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-2 leading-snug">{note}</p>}
      <span className={`absolute left-0 right-0 bottom-0 h-[3px] ${t.bar}`} />
    </Panel>
  );
};

export const StatRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">{children}</div>
);

const PILL_TONES = {
  blue:    'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  violet:  'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  amber:   'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  rose:    'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  cyan:    'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  slate:   'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300',
};

export const Pill = ({ tone = 'slate', dot = false, className = '', children }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap
                    ${PILL_TONES[tone] || PILL_TONES.slate} ${className}`}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
    {children}
  </span>
);

export const btn = {
  primary: 'inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold text-white ' +
           'bg-blue-600 hover:bg-blue-700 transition shadow-sm',
  quiet:   'inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold ' +
           'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition ' +
           'dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60',
  green:   'inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold text-white ' +
           'bg-emerald-600 hover:bg-emerald-700 transition shadow-sm',
  icon:    'grid place-items-center w-8 h-8 rounded-lg border border-slate-200 text-slate-500 ' +
           'hover:bg-slate-50 hover:text-slate-700 transition ' +
           'dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-100',
  iconDanger: 'grid place-items-center w-8 h-8 rounded-lg border border-rose-200 text-rose-600 ' +
           'hover:bg-rose-50 transition dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10',
};

export const field = 'h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 ' +
  'border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 ' +
  'placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition';

/** A table that scrolls sideways on small screens, with the console's header row. */
export const Table = ({ head = [], children, empty, colSpan }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full">
      <thead>
        <tr className="border-b border-slate-200 dark:border-slate-800">
          {head.map((h, i) => (
            <th
              key={i}
              className={`px-4 sm:px-5 py-3 text-[10.5px] font-black uppercase tracking-[0.1em]
                          text-slate-400 dark:text-slate-500 whitespace-nowrap
                          ${h.align === 'right' ? 'text-right' : 'text-left'}`}
              style={h.width ? { width: h.width } : undefined}
            >
              {h.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
        {React.Children.count(children) > 0 ? children : (
          <tr><td colSpan={colSpan || head.length} className="px-5 py-14 text-center text-sm text-slate-400">{empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const Row = ({ children, className = '', ...rest }) => (
  <tr className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition ${className}`} {...rest}>{children}</tr>
);

export const Cell = ({ className = '', children, ...rest }) => (
  <td className={`px-4 sm:px-5 py-3.5 text-[13px] text-slate-700 dark:text-slate-300 ${className}`} {...rest}>
    {children}
  </td>
);

/** Numbered pager — shows the first pages, an ellipsis, then the last. */
export const Pagination = ({ page, pageCount, onPage, summary }) => {
  if (pageCount <= 1) return summary ? <div className="px-5 py-3.5 text-[12px] text-slate-500 dark:text-slate-400">{summary}</div> : null;
  const pages = [];
  for (let i = 1; i <= pageCount; i += 1) {
    if (i <= 3 || i === pageCount || Math.abs(i - page) <= 1) pages.push(i);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  const step = 'grid place-items-center min-w-[34px] h-9 px-2 rounded-lg text-[12.5px] font-bold transition';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-100 dark:border-slate-800">
      <p className="text-[12px] text-slate-500 dark:text-slate-400">{summary}</p>
      <div className="flex items-center gap-1.5">
        <button type="button" disabled={page === 1} onClick={() => onPage(page - 1)}
          className={`${step} border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300
                      disabled:opacity-40 disabled:cursor-default hover:bg-slate-50 dark:hover:bg-slate-800`}>
          <ChevronLeft size={15} />
        </button>
        {pages.map((p, i) => (p === '…' ? (
          <span key={`gap${i}`} className="px-1 text-slate-400">…</span>
        ) : (
          <button key={p} type="button" onClick={() => onPage(p)}
            className={`${step} ${p === page
              ? 'bg-blue-600 text-white'
              : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
            {p}
          </button>
        )))}
        <button type="button" disabled={page === pageCount} onClick={() => onPage(page + 1)}
          className={`${step} border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300
                      disabled:opacity-40 disabled:cursor-default hover:bg-slate-50 dark:hover:bg-slate-800`}>
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

/** Round monogram used beside a name in the lists. */
export const Avatar = ({ name = '', tone = 'blue' }) => {
  const initials = String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  return (
    <span className={`grid place-items-center w-9 h-9 rounded-xl text-[12px] font-black flex-shrink-0
                      ${PILL_TONES[tone] || PILL_TONES.blue}`}>
      {initials}
    </span>
  );
};

/* ── dropdowns ───────────────────────────────────────────────────────────────
   A native <select> and a bare button never matched the console, so both the
   single and the multi pickers are built here: same shell, same popover, with
   a filter box once the list gets long. */

const useAway = (ref, onAway) => {
  React.useEffect(() => {
    const onDown = (e) => {
      // The menu renders in a portal, so it is outside `ref`; its own handler
      // stops the event before this one sees it.
      if (ref.current && !ref.current.contains(e.target)) onAway();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ref, onAway]);
};

const popoverShell = 'rounded-xl overflow-hidden border border-slate-200 bg-white shadow-2xl ' +
  'dark:border-slate-700 dark:bg-slate-900';

/**
 * Menus used to be absolutely positioned, so a dialog's scrolling body or a
 * panel with hidden overflow cut them off. They are placed against the window
 * instead, flipping above the trigger when there is no room below.
 */
const Popover = ({ anchorRef, align = 'left', onClose, children }) => {
  const [box, setBox] = useState(null);

  React.useLayoutEffect(() => {
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(Math.max(r.width, 240), window.innerWidth - 24);
      const below = window.innerHeight - r.bottom;
      const up = below < 300 && r.top > below;
      setBox({
        top: up ? undefined : r.bottom + 8,
        bottom: up ? window.innerHeight - r.top + 8 : undefined,
        left: align === 'right' ? Math.max(12, r.right - width) : Math.min(r.left, window.innerWidth - width - 12),
        width,
        maxHeight: Math.max(180, (up ? r.top : below) - 24),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, align]);

  if (!box) return null;
  return createPortal(
    <div
      className={`fixed z-[90] flex flex-col ${popoverShell}`}
      style={{ top: box.top, bottom: box.bottom, left: box.left, width: box.width, maxHeight: box.maxHeight }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

// h-11 so a picker lines up with `field` wherever the two sit in one row.
const trigger = (open) => 'inline-flex items-center justify-between gap-2 h-11 px-3.5 rounded-xl text-sm font-bold w-full ' +
  'border transition outline-none ' +
  (open
    ? 'border-blue-400 ring-4 ring-blue-500/10 bg-white text-slate-800 dark:bg-slate-800/70 dark:text-slate-100 dark:border-blue-500/60'
    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 ' +
      'dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60');

const OptionRow = ({ active, children, ...rest }) => (
  <button type="button"
    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] transition
                ${active
                  ? 'bg-blue-50 text-blue-700 font-bold dark:bg-blue-500/15 dark:text-blue-300'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800'}`}
    {...rest}
  >
    {children}
  </button>
);

const Filterable = ({ options, filter, setFilter, children }) => (
  <>
    {options.length > 3 && (
      <div className="p-2 border-b border-slate-100 dark:border-slate-800">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Type to filter…"
            className="w-full h-9 pl-8 pr-2.5 rounded-lg text-[13px] bg-slate-50 dark:bg-slate-800/70
                       border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100
                       placeholder-slate-400 outline-none focus:border-blue-400"
          />
        </div>
      </div>
    )}
    <div className="flex-1 overflow-y-auto py-1 min-h-0">{children}</div>
  </>
);

/** One choice, or none. `options` is [{ value, label }] or plain strings. */
export const Select = ({
  value, onChange, options = [], allLabel = 'All',
  className = '', icon: Icon, align = 'left',
  // A required field has no "any of them" state, so it offers no clear row and
  // falls back to its first option rather than repeating it as the all-label.
  clearable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = React.useRef(null);
  useAway(ref, () => setOpen(false));

  const items = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const shown = filter.trim()
    ? items.filter((o) => o.label.toLowerCase().includes(filter.trim().toLowerCase()))
    : items;
  const current = items.find((o) => o.value === value) || (clearable ? null : items[0]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" className={trigger(open)} onClick={() => setOpen((v) => !v)}>
        <span className="inline-flex items-center gap-2 min-w-0">
          {Icon && <Icon size={15} className="text-slate-400 flex-shrink-0" />}
          <span className={`truncate ${current ? '' : 'text-slate-500 dark:text-slate-400 font-semibold'}`}>
            {current ? current.label : allLabel}
          </span>
        </span>
        <ChevronDown size={15} className={`flex-shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <Popover anchorRef={ref} align={align} onClose={() => setOpen(false)}>
          <Filterable options={items} filter={filter} setFilter={setFilter}>
            {clearable && (
              <OptionRow active={!value} onClick={() => { onChange(''); setOpen(false); }}>
                <span className="w-4">{!value && <Check size={14} />}</span>
                {allLabel}
              </OptionRow>
            )}
            {shown.map((o) => {
              const on = o.value === (current ? current.value : value);
              return (
                <OptionRow key={o.value} active={on}
                  onClick={() => { onChange(o.value); setOpen(false); }}>
                  <span className="w-4">{on && <Check size={14} />}</span>
                  <span className="truncate">{o.label}</span>
                </OptionRow>
              );
            })}
            {shown.length === 0 && <p className="px-3 py-6 text-center text-[13px] text-slate-400">Nothing matches</p>}
          </Filterable>
        </Popover>
      )}
    </div>
  );
};

/** Several choices. `selected` is an array of values. */
export const MultiSelect = ({
  selected = [], onToggle, onClear, options = [],
  allLabel = 'All', nounPlural = 'selected', className = '', align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = React.useRef(null);
  useAway(ref, () => setOpen(false));

  const items = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const shown = filter.trim()
    ? items.filter((o) => o.label.toLowerCase().includes(filter.trim().toLowerCase()))
    : items;
  const label = selected.length === 0 ? allLabel
    : selected.length === 1 ? selected[0]
    : `${selected.length} ${nounPlural}`;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" className={trigger(open)} onClick={() => setOpen((v) => !v)}>
        <span className={`truncate ${selected.length ? '' : 'text-slate-500 dark:text-slate-400 font-semibold'}`}>{label}</span>
        <span className="flex items-center gap-1.5 flex-shrink-0">
          {selected.length > 1 && (
            <span className="px-1.5 rounded-md bg-blue-100 text-blue-700 text-[11px] dark:bg-blue-500/20 dark:text-blue-300">
              {selected.length}
            </span>
          )}
          <ChevronDown size={15} className={`text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <Popover anchorRef={ref} align={align} onClose={() => setOpen(false)}>
          <Filterable options={items} filter={filter} setFilter={setFilter}>
            {shown.map((o) => {
              const on = selected.some((v) => String(v).toLowerCase() === String(o.value).toLowerCase());
              return (
                <OptionRow key={o.value} active={on} onClick={() => onToggle(o.value)}>
                  <span className={`grid place-items-center w-4 h-4 rounded border flex-shrink-0 ${on
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-slate-300 dark:border-slate-600'}`}>
                    {on && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </OptionRow>
              );
            })}
            {shown.length === 0 && <p className="px-3 py-6 text-center text-[13px] text-slate-400">Nothing matches</p>}
          </Filterable>
          {selected.length > 0 && (
            <button type="button" onClick={() => { onClear(); setOpen(false); }}
              className="w-full px-3 py-2.5 text-[12.5px] font-bold text-slate-500 hover:text-slate-700
                         border-t border-slate-100 hover:bg-slate-50
                         dark:border-slate-800 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800">
              Clear selection
            </button>
          )}
        </Popover>
      )}
    </div>
  );
};

/* ── dialog ──────────────────────────────────────────────────────────────── */

/** Centred dialog with the console's chrome. `footer` pins to the bottom. */
export const Modal = ({ open, onClose, icon: Icon, tone = 'blue', title, subtitle, width = 'max-w-lg', footer, children }) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const ring = TONES[tone] || TONES.blue;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}>
      <div
        className={`w-full ${width} max-h-[92vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden
                    bg-white border border-slate-200 dark:bg-[#0e1626] dark:border-slate-800`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          {Icon && <span className={`grid place-items-center w-10 h-10 rounded-xl flex-shrink-0 ${ring.ring}`}><Icon size={19} /></span>}
          <div className="min-w-0 flex-1">
            <h3 className="text-[16px] font-bold text-slate-900 dark:text-white">{title}</h3>
            {subtitle && <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600
                       dark:hover:bg-slate-800 dark:hover:text-slate-200 transition">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/** Label + control + error, the way every console form stacks them. */
export const FormField = ({ label, required, hint, error, className = '', children }) => (
  <div className={className}>
    <label className="flex items-baseline gap-1.5 mb-1.5">
      <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300">{label}</span>
      {required && <span className="text-rose-500">*</span>}
      {hint && <span className="text-[11px] font-medium text-slate-400">{hint}</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-[11.5px] font-semibold text-rose-500">{error}</p>}
  </div>
);

/** A labelled block of fields inside a form. */
export const FormSection = ({ title, description, icon: Icon, tone = 'slate', children }) => {
  const ring = TONES[tone] || TONES.slate;
  return (
    <section className="py-5 first:pt-0 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="flex items-start gap-3 mb-4">
        {Icon && <span className={`grid place-items-center w-8 h-8 rounded-lg flex-shrink-0 ${ring.ring}`}><Icon size={16} /></span>}
        <div>
          <h4 className="text-[13.5px] font-bold text-slate-900 dark:text-white">{title}</h4>
          {description && <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
    </section>
  );
};

/** The "are you sure" dialog, in one place so every delete looks the same. */
export const ConfirmModal = ({
  open, onClose, onConfirm, busy = false,
  title = 'Are you sure?', subtitle, icon = AlertTriangle, tone = 'rose',
  confirmLabel = 'Delete', busyLabel = 'Deleting…', cancelLabel = 'Cancel',
  children,
}) => (
  <Modal
    open={open}
    onClose={busy ? () => {} : onClose}
    icon={icon}
    tone={tone}
    title={title}
    subtitle={subtitle}
    width="max-w-md"
    footer={(
      <div className="flex flex-col-reverse sm:flex-row gap-2.5">
        <button type="button" onClick={onClose} disabled={busy} className={`${btn.quiet} disabled:opacity-50`}>
          {cancelLabel}
        </button>
        <button type="button" onClick={onConfirm} disabled={busy}
          className={`inline-flex items-center justify-center gap-2 h-10 px-4 flex-1 rounded-xl text-sm font-bold text-white transition disabled:opacity-60
                      ${tone === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
          {busy ? busyLabel : confirmLabel}
        </button>
      </div>
    )}
  >
    {children}
  </Modal>
);
